import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Container,
  useTheme,
  Fade,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const features = [
    {
      icon: <SearchIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Consulta Rápida',
      description: 'Consulte seus valores devidos em segundos usando apenas seu CPF',
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Documentos Automáticos',
      description: 'Geração automática de documentos oficiais para solicitação',
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Transparência Total',
      description: 'Detalhamento completo de todos os cálculos e descontos',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Segurança Garantida',
      description: 'Seus dados são protegidos com criptografia de ponta a ponta',
    },
  ];

  return (
    <Fade in={true} timeout={800}>
      <Box>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            py: 8,
            mb: 6,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Container maxWidth="md">
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              SINTET - Sindicato dos trabalhadores em Educação no Estado do Tocantins
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              Sistema de Consulta de Valores Devidos
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Consulte, solicite e acompanhe o recebimento dos seus valores devidos 
              de forma rápida, segura e transparente.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => navigate('/consulta')}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 2,
              }}
            >
              Consultar Meus Valores
            </Button>
          </Container>
        </Box>

        {/* Features */}
        <Typography variant="h4" textAlign="center" gutterBottom color="primary.main">
          Como Funciona
        </Typography>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Um processo simples em apenas 3 passos
        </Typography>

        {/* Steps */}
        <Box sx={{ mb: 8 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    width: '100%',
                    height: 300,
                    backgroundColor: theme.palette.primary.light,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SpeedIcon sx={{ fontSize: 100, color: 'white', opacity: 0.7 }} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 3 }}>
                {[
                  { step: '01', title: 'Consulta', desc: 'Digite seu CPF para consultar os valores' },
                  { step: '02', title: 'Validação', desc: 'Confirme atravez do seu e-mail para gerar seu documento' },
                  { step: '03', title: 'Recebimento', desc: 'Assine e acompanhe seu processo' },
                ].map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      {item.step}
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={3} key={index}>
              <Fade in={true} timeout={(index + 1) * 300}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: 3,
                    borderRadius: 3,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
            color: 'white',
            py: 6,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Container maxWidth="md">
            <SupportAgentIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Precisa de Ajuda?
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              Nossa equipe está pronta para ajudar você em todas as etapas do processo.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/consulta')}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 2,
                backgroundColor: 'white',
                color: theme.palette.secondary.main,
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              Iniciar Consulta Agora
            </Button>
          </Container>
        </Box>
      </Box>
    </Fade>
  );
};

export default HomePage;